import React, { useState, useEffect } from 'react';
import { Plus, Search, Package, Edit2, Trash2, LayoutGrid, List } from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { ManagedArticle, AccountingAccount } from '../../types';
import { subscribeToArticles, createArticle, updateArticle, deleteArticle } from '../../services/managementService';

interface ArticleCatalogProps {
  userId: string;
  accountingAccounts: AccountingAccount[];
}

export const ArticleCatalog: React.FC<ArticleCatalogProps> = ({ userId, accountingAccounts }) => {
  const [articles, setArticles] = useState<ManagedArticle[]>([]);
  const [search, setSearch] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingArticle, setEditingArticle] = useState<ManagedArticle | null>(null);
  
  // Form state
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('');
  const [accountId, setAccountId] = useState('');

  useEffect(() => {
    return subscribeToArticles(userId, setArticles);
  }, [userId]);

  useEffect(() => {
    if (editingArticle) {
      setName(editingArticle.name);
      setDescription(editingArticle.description);
      setCategory(editingArticle.category);
      setAccountId(editingArticle.accountingAccountId);
    } else {
      setName('');
      setDescription('');
      setCategory('');
      setAccountId('');
    }
  }, [editingArticle]);

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name || !accountId) return;

    const data = {
      name,
      description,
      category,
      accountingAccountId: accountId
    };

    try {
      if (editingArticle) {
        await updateArticle(userId, editingArticle.id, data);
      } else {
        await createArticle(userId, data);
      }
      setShowForm(false);
      setEditingArticle(null);
    } catch (error) {
      console.error("Error saving article:", error);
      alert("Error al guardar el artículo.");
    }
  };

  const filteredArticles = articles.filter(a => 
    a.name.toLowerCase().includes(search.toLowerCase()) || 
    a.description.toLowerCase().includes(search.toLowerCase()) ||
    a.category.toLowerCase().includes(search.toLowerCase())
  );

  const leafAccounts = accountingAccounts.filter(a => a.isSelectable);

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" size={18} />
          <input 
            type="text"
            placeholder="Buscar artículos..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full bg-zinc-900 border border-zinc-800 rounded-xl pl-10 pr-4 py-2 text-sm focus:ring-2 focus:ring-emerald-500/50 outline-none transition-all"
          />
        </div>
        <button 
          onClick={() => {
            setEditingArticle(null);
            setShowForm(true);
          }}
          className="flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white px-6 py-2 rounded-xl font-bold transition-all whitespace-nowrap"
        >
          <Plus size={18} />
          Nuevo Artículo
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filteredArticles.map(article => {
          const account = accountingAccounts.find(a => a.id === article.accountingAccountId);
          return (
            <motion.div 
              key={article.id}
              layout
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="bg-zinc-900 border border-zinc-800 p-5 rounded-2xl group hover:border-zinc-700 transition-all"
            >
              <div className="flex justify-between items-start mb-3">
                <div className="w-10 h-10 bg-emerald-500/10 rounded-xl flex items-center justify-center border border-emerald-500/20">
                  <Package className="text-emerald-500" size={20} />
                </div>
                <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-all">
                  <button 
                    onClick={() => {
                      setEditingArticle(article);
                      setShowForm(true);
                    }}
                    className="p-1.5 text-zinc-400 hover:text-emerald-400 hover:bg-emerald-500/10 rounded-lg transition-all"
                  >
                    <Edit2 size={14} />
                  </button>
                  <button 
                    onClick={() => {
                      if(window.confirm('¿Eliminar artículo?')) deleteArticle(userId, article.id);
                    }}
                    className="p-1.5 text-zinc-400 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-all"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
              <h3 className="font-bold text-white mb-1">{article.name}</h3>
              <p className="text-xs text-zinc-500 line-clamp-2 mb-4">{article.description || 'Sin descripción'}</p>
              
              <div className="space-y-2 border-t border-zinc-800 pt-4">
                <div className="flex items-center justify-between">
                  <span className="text-[10px] text-zinc-500 uppercase font-bold tracking-wider">Categoría</span>
                  <span className="text-[11px] bg-zinc-800 px-2 py-0.5 rounded-md text-zinc-300 font-medium">{article.category || 'Varios'}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-[10px] text-zinc-500 uppercase font-bold tracking-wider">Cuenta Contable</span>
                  <span className="text-[11px] text-emerald-500 font-medium truncate ml-4" title={account?.name}>
                    {account ? `${account.code} - ${account.name}` : 'No vinculada'}
                  </span>
                </div>
              </div>
            </motion.div>
          );
        })}
      </div>

      <AnimatePresence>
        {showForm && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
            <motion.div 
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              className="bg-zinc-900 border border-zinc-800 rounded-3xl w-full max-w-lg overflow-hidden shadow-2xl"
            >
              <div className="p-6 border-b border-zinc-800 flex items-center justify-between bg-zinc-800/20">
                <h2 className="text-xl font-bold text-white">{editingArticle ? 'Editar' : 'Nuevo'} Artículo</h2>
                <button onClick={() => setShowForm(false)} className="p-2 hover:bg-zinc-800 rounded-xl transition-all">
                  <Trash2 size={20} className="rotate-45" />
                </button>
              </div>

              <form onSubmit={handleSave} className="p-6 space-y-4">
                <div className="space-y-2">
                  <label className="text-xs font-bold text-zinc-500 uppercase tracking-widest px-1">Nombre del Artículo</label>
                  <input 
                    required
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="w-full bg-zinc-950 border border-zinc-800 rounded-xl px-4 py-3 text-sm focus:ring-2 focus:ring-emerald-500/50 outline-none transition-all"
                    placeholder="Ej: Artículos de Limpieza, Coca Cola 2L, etc."
                  />
                </div>

                <div className="space-y-2">
                  <label className="text-xs font-bold text-zinc-500 uppercase tracking-widest px-1">Descripción (Opcional)</label>
                  <textarea 
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    className="w-full bg-zinc-950 border border-zinc-800 rounded-xl px-4 py-3 text-sm focus:ring-2 focus:ring-emerald-500/50 outline-none transition-all resize-none"
                    rows={2}
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <label className="text-xs font-bold text-zinc-500 uppercase tracking-widest px-1">Categoría</label>
                    <input 
                      value={category}
                      onChange={(e) => setCategory(e.target.value)}
                      className="w-full bg-zinc-950 border border-zinc-800 rounded-xl px-4 py-3 text-sm focus:ring-2 focus:ring-emerald-500/50 outline-none transition-all"
                      placeholder="Ej: Supermercado"
                    />
                  </div>

                  <div className="space-y-2">
                    <label className="text-xs font-bold text-zinc-500 uppercase tracking-widest px-1">Cuenta Contable</label>
                    <select 
                      required
                      value={accountId}
                      onChange={(e) => setAccountId(e.target.value)}
                      className="w-full bg-zinc-950 border border-zinc-800 rounded-xl px-4 py-3 text-sm focus:ring-2 focus:ring-emerald-500/50 outline-none transition-all"
                    >
                      <option value="">Seleccionar cuenta...</option>
                      {leafAccounts.map(acc => (
                        <option key={acc.id} value={acc.id}>{acc.code} - {acc.name}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="pt-4 flex gap-3">
                  <button 
                    type="button"
                    onClick={() => setShowForm(false)}
                    className="flex-1 py-3 bg-zinc-800 hover:bg-zinc-700 text-white font-bold rounded-xl transition-all"
                  >
                    Cancelar
                  </button>
                  <button 
                    type="submit"
                    className="flex-1 py-3 bg-emerald-600 hover:bg-emerald-700 text-white font-bold rounded-xl transition-all shadow-lg shadow-emerald-600/20"
                  >
                    {editingArticle ? 'Actualizar' : 'Crear'} Artículo
                  </button>
                </div>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};
